import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { setPageMeta } from "@/lib/pageMeta";
import { RegistrationForm } from "@/components/RegistrationForm";
import { PatientStories } from "@/components/PatientStories";
import {
  CheckCircle2, Loader2, ArrowRight, Heart, Utensils, Video,
  ClipboardList, BarChart3, Calendar, Shield, MessageCircle
} from "lucide-react";

const TITO_PHOTO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_ec3092a6-9515-414f-b9ed-3aef1b852820/artifacts/ffcc76ff3688e3cf_New%20headshot%20.jpg";
const WHATSAPP_ENQUIRY = "https://wa.me/2347062039281?text=Hi%2C%20I%E2%80%99d%20like%20to%20learn%20more%20about%20the%20PlateMind%2012-week%20programme.";

const ProgrammeLanding = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  const payRef = searchParams.get("reference") || searchParams.get("trxref");

  useEffect(() => {
    setPageMeta({ title: "12-Week Cardiometabolic Health Programme | PlateMind", description: "Personalised nutrition support for diabetes, high blood pressure, high cholesterol and weight management. Work with a dietitian for ₦12,000 for 12 weeks.", image: "/og-programme.jpg" });
    fetchInfo(); if (payRef) verifyPayment(payRef);
  }, []);

  const fetchInfo = async () => {
    try { const r = await fetch(`${API}/programme/info`); if (r.ok) setInfo(await r.json()); } catch {}
    finally { setLoading(false); }
  };

  const verifyPayment = async (ref) => {
    setVerifying(true);
    try {
      const r = await fetch(`${API}/programme/verify-payment?reference=${ref}`);
      if (r.ok) { const d = await r.json(); setPaymentResult(d); if (d.status === "successful") toast.success("Payment confirmed. Welcome to PlateMind!"); }
      else { const e = await r.json(); setPaymentResult({ status: "failed", message: e.detail || "Verification failed" }); }
    } catch { setPaymentResult({ status: "failed", message: "Connection error" }); }
    finally { setVerifying(false); }
  };

  const fmtPrice = (a, c) => c === "NGN" ? `\u20A6${a?.toLocaleString()}` : `${c} ${a?.toLocaleString()}`;

  if (loading) return <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF7A00]" /></div>;

  // Payment verification
  if (verifying || paymentResult) return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="programme-page">
      <SiteHeader />
      <div className="h-16" />
      <main className="max-w-2xl mx-auto px-5 py-20 text-center space-y-6">
        {verifying ? (
          <><Loader2 className="w-10 h-10 animate-spin text-[#FF7A00] mx-auto" /><h1 className="text-xl font-bold text-stone-900">Verifying your payment...</h1></>
        ) : paymentResult?.status === "successful" ? (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-green-600" /></div>
            <h1 className="text-2xl font-bold text-stone-900">Payment confirmed</h1>
            <p className="text-stone-600">You are now enrolled. Let's book your first consultation.</p>
            <Button onClick={() => navigate(paymentResult.redirect_to || "/consultation?type=initial")} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl px-8 py-3 font-semibold" data-testid="go-to-booking-btn">
              Book your consultation <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto"><Heart className="w-8 h-8 text-red-500" /></div>
            <h1 className="text-xl font-bold text-stone-900">Payment not confirmed</h1>
            <p className="text-sm text-stone-500">{paymentResult?.message}</p>
            <Button variant="outline" onClick={() => { setPaymentResult(null); window.history.replaceState({}, "", "/programme"); }} className="rounded-xl" data-testid="try-again-btn">Try again</Button>
          </>
        )}
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="programme-page">
      <SiteHeader />
      <div className="h-16" />

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-stone-900 to-stone-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 50%, #FF7A00 0%, transparent 50%)" }} />
        <div className="max-w-5xl mx-auto px-5 py-16 sm:py-24 relative z-10">
          <div className="max-w-2xl space-y-5">
            <span className="inline-block text-xs font-semibold tracking-[0.15em] uppercase text-[#FF7A00]" data-testid="hero-eyebrow">12-Week Cardiometabolic Health Programme</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight">
              Don't wait for your health to get worse before you make a change.
            </h1>
            <p className="text-base sm:text-lg text-stone-300 leading-relaxed">
              Get personalised nutrition support for diabetes, high blood pressure, high cholesterol and weight management. Work with a dietitian to build a practical plan around your food, lifestyle and health needs.
            </p>
            <div className="flex items-end gap-2 pt-1">
              <span className="text-4xl sm:text-5xl font-extrabold text-[#FF7A00]">{fmtPrice(info?.price, info?.currency)}</span>
              <span className="text-stone-400 text-sm mb-1.5">for 12 weeks</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                onClick={() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl px-8 py-3 text-base font-semibold"
                data-testid="hero-cta-pay"
              >
                Take the First Step <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a href={WHATSAPP_ENQUIRY} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-xl px-8 py-3 text-base font-medium w-full sm:w-auto" data-testid="hero-cta-enquiry">
                  <MessageCircle className="w-4 h-4 mr-2" /> Ask Us a Question
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sub-headline */}
      <section className="bg-white border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-5 py-12 text-center">
          <p className="text-lg sm:text-xl text-stone-700 leading-relaxed font-medium">
            Take control of your blood sugar, blood pressure, cholesterol and weight with personalised nutrition support, culturally familiar meals and a dietitian-guided plan built around your life.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="max-w-5xl mx-auto px-5 py-14 sm:py-18">
        <h2 className="text-2xl font-bold text-stone-900 mb-8 text-center">What is included</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <IncludeCard icon={Video} title="Initial consultation" desc="A 45-minute video call with your dietitian to discuss your health, goals and eating habits." />
          <IncludeCard icon={Utensils} title="Personalised meal plan" desc="A 14-day plan built from real African foods, tailored to your calorie target and preferences." />
          <IncludeCard icon={ClipboardList} title="10 weekly check-ins" desc="Track your progress each week. Your dietitian reviews every response." />
          <IncludeCard icon={BarChart3} title="Clinical reports" desc="Detailed reports on your progress, written in language you can understand." />
          <IncludeCard icon={Calendar} title="Follow-up consultations" desc="Book time with your dietitian whenever you need extra support during the programme." />
          <IncludeCard icon={Heart} title="Discharge and next steps" desc="A final consultation at Week 12 with clear guidance on what comes next." />
        </div>
      </section>

      {/* Who it's for */}
      <section className="bg-stone-50 border-y border-stone-100">
        <div className="max-w-5xl mx-auto px-5 py-14 sm:py-18">
          <h2 className="text-2xl font-bold text-stone-900 mb-3 text-center">Who is this for?</h2>
          <p className="text-sm text-stone-500 text-center max-w-xl mx-auto mb-10">If any of these sound familiar, this programme could be the right fit for you.</p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              "You have been told your blood sugar, cholesterol or blood pressure needs attention",
              "You want to eat better but are not sure where to start with African food",
              "You have tried diets that did not fit your lifestyle, budget or cultural preferences",
              "You want practical, sustained changes rather than quick fixes",
              "You have a family history of diabetes, heart disease or stroke",
              "You want professional support from someone who understands your food",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3">
                <CheckCircle2 className="w-5 h-5 text-[#FF7A00] shrink-0 mt-0.5" />
                <p className="text-sm text-stone-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-5 py-14 sm:py-18">
        <h2 className="text-2xl font-bold text-stone-900 mb-10 text-center">How it works</h2>
        <div className="grid sm:grid-cols-4 gap-6 max-w-4xl mx-auto">
          <StepCard num="01" title="Register and pay" desc="Complete a short registration and make a one-time payment." />
          <StepCard num="02" title="Book your consultation" desc="Choose a time for your initial 45-minute video consultation." />
          <StepCard num="03" title="Start your programme" desc="Receive your personalised meal plan and begin weekly check-ins." />
          <StepCard num="04" title="See the difference" desc="Track your progress over 12 weeks with ongoing dietitian support." />
        </div>
      </section>

      {/* Meet Your Dietitian */}
      <section className="bg-white border-y border-stone-100">
        <div className="max-w-5xl mx-auto px-5 py-14 sm:py-18">
          <h2 className="text-2xl font-bold text-stone-900 mb-10 text-center">Meet your lead dietitian</h2>
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start max-w-4xl mx-auto">
            <div className="lg:w-72 shrink-0 mx-auto lg:mx-0">
              <img
                src={TITO_PHOTO}
                alt="Tito Ipinmoye, Clinical Dietitian"
                className="w-56 h-72 sm:w-64 sm:h-80 object-cover object-top rounded-2xl shadow-lg"
                data-testid="dietitian-photo"
              />
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-stone-900">Tito Ipinmoye</h3>
                <p className="text-sm text-[#FF7A00] font-medium">Clinical Dietitian and HealthTech Product Builder</p>
              </div>
              <p className="text-sm text-stone-600 leading-relaxed">
                With nearly a decade of experience in clinical nutrition, Tito Ipinmoye has worked with patients across hospital and clinical settings in Nigeria, supporting people living with diabetes, obesity, cardiovascular disease and other metabolic conditions. With a background in Human Nutrition and Dietetics and additional training in Culinary Arts, she brings together clinical knowledge, practical food skills and a deep understanding of how nutrition advice needs to work in real life.
              </p>
              <p className="text-sm text-stone-600 leading-relaxed">
                Her approach is built around a simple belief: managing your health should not mean giving up the food you love or following a plan that makes everyday life harder. It should mean understanding your body, making informed choices and finding an approach to food that fits your culture, circumstances and health goals.
              </p>
              <p className="text-sm text-stone-600 leading-relaxed">
                As the founder of The Nutrition Network Africa, Tito is working to make personalised nutrition support more accessible through technology, combining AI-assisted nutritional planning with the expertise and oversight of qualified healthcare professionals. Her goal is to help people make sense of their health, build sustainable eating habits and navigate cardiometabolic care with greater confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PatientStories />

      {/* Registration Form */}
      <section id="register" className="bg-stone-900 scroll-mt-16">
        <div className="max-w-xl mx-auto px-5 py-14 sm:py-18">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Start my 12-week journey</h2>
            <p className="text-sm text-stone-400">Complete your registration below. You will be directed to our secure payment page.</p>
          </div>

          <Card className="rounded-2xl border-stone-700 bg-stone-800 p-6 sm:p-8" data-testid="registration-form">
            <RegistrationForm idPrefix="reg" />
          </Card>
        </div>
      </section>

      {/* FAQ / Objections */}
      <section className="max-w-3xl mx-auto px-5 py-14 sm:py-18">
        <h2 className="text-2xl font-bold text-stone-900 mb-8 text-center">Common questions</h2>
        <div className="space-y-4">
          <FAQ q="Are the consultations online?" a="Yes. All consultations are conducted through video call (Google Meet). You can join from anywhere with an internet connection." />
          <FAQ q="What kind of support do I get during the 12 weeks?" a="You get an initial consultation, a personalised meal plan, 10 weekly check-ins with your dietitian, clinical reports, and the option to book follow-up consultations whenever you need extra support." />
          <FAQ q="Do I have to change everything about how I eat?" a="No. Your meal plan is built around African foods you already know and enjoy. We work with your preferences, your budget and your routine." />
          <FAQ q="What happens after the 12 weeks?" a="At Week 12, you have a final consultation with your dietitian to review your progress and discuss next steps. You can also choose to renew for an additional 6 weeks." />
          <FAQ q="Can I cancel or get a refund?" a="Please contact us before your initial consultation if you need to cancel. We will work with you to find a solution." />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-8 border-t border-stone-800">
        <div className="max-w-5xl mx-auto px-5 text-center text-xs space-y-3">
          <img src="/platemind-logo-new.png" alt="PlateMind" className="h-7 w-auto mx-auto brightness-0 invert" />
          <p>AI for everyday cardiometabolic meal decisions</p>
        </div>
      </footer>
    </div>
  );
};

const IncludeCard = ({ icon: Icon, title, desc }) => (
  <Card className="rounded-2xl border-stone-200 p-5 space-y-3 hover:shadow-md transition-shadow">
    <div className="w-10 h-10 rounded-xl bg-[#FFF7F0] flex items-center justify-center"><Icon className="w-5 h-5 text-[#FF7A00]" /></div>
    <h3 className="text-base font-bold text-stone-900">{title}</h3>
    <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
  </Card>
);

const StepCard = ({ num, title, desc }) => (
  <div className="text-center space-y-3">
    <span className="inline-block text-xs font-bold text-[#FF7A00] bg-[#FFF7F0] rounded-full px-3 py-1">{num}</span>
    <h3 className="text-sm font-bold text-stone-900">{title}</h3>
    <p className="text-xs text-stone-500 leading-relaxed">{desc}</p>
  </div>
);

const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card className="rounded-xl border-stone-200 overflow-hidden">
      <button className="w-full text-left px-5 py-4 flex items-center justify-between" onClick={() => setOpen(!open)}>
        <span className="text-sm font-semibold text-stone-900">{q}</span>
        <span className={`text-stone-400 transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && <div className="px-5 pb-4 text-sm text-stone-600 leading-relaxed">{a}</div>}
    </Card>
  );
};

export default ProgrammeLanding;
